
import React from 'react';
import { DndContext, DragEndEvent, DragOverlay } from '@dnd-kit/core';
import { createPortal } from 'react-dom';
import StepColumn from './StepColumn';
import StepManager from './StepManager';
import DraggableSubtask from './DraggableSubtask';
import { ProjectSubtask, StepColumn as StepColumnType } from '../types';

interface StepsContainerProps {
  subtasks: ProjectSubtask[];
  selectedTaskId: string | null;
  isLoading: boolean;
  onEdit: (subtask: ProjectSubtask) => void;
  onDelete: (subtaskId: string) => void;
  onStatusChange: (subtaskId: string, status: 'To Do' | 'In Progress' | 'Done') => void;
  onMoveSubtask: (subtaskId: string, newStep: number, newStepName: string) => void;
  onAddStep: (stepName: string) => void;
  onEditStep: (step: number, newName: string) => void;
  onDeleteStep: (step: number) => void;
  availableSteps: { step: number; step_name: string }[];
  isRestrictedUser?: boolean;
}

const StepsContainer = ({
  subtasks,
  selectedTaskId,
  isLoading,
  onEdit,
  onDelete,
  onStatusChange,
  onMoveSubtask,
  onAddStep,
  onEditStep,
  onDeleteStep,
  availableSteps,
  isRestrictedUser = false
}: StepsContainerProps) => {
  const [activeSubtask, setActiveSubtask] = React.useState<ProjectSubtask | null>(null);

  if (!selectedTaskId) {
    return (
      <div className="p-6 text-center">
        <div className="text-gray-300 text-sm mb-3">
          Select a task from the progress overview to view and manage its subtasks.
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6 text-center">
        <div className="text-gray-300 text-sm">Loading subtasks...</div>
      </div>
    );
  }

  // Create step columns from available steps - show all steps for owners, only non-empty for restricted users
  const stepColumns: StepColumnType[] = availableSteps
    .map(({ step, step_name }) => {
      const stepSubtasks = subtasks.filter(subtask => (subtask.step || 1) === step);
      return {
        step,
        step_name,
        subtasks: stepSubtasks
      };
    })
    .filter(stepColumn => !isRestrictedUser || stepColumn.subtasks.length > 0);

  const handleDragStart = (event: any) => {
    const { active } = event;
    const subtask = subtasks.find(s => s.id === active.id);
    setActiveSubtask(subtask || null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveSubtask(null);

    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    // Check if dropping on a step column
    if (overId.startsWith('step-')) {
      const newStep = parseInt(overId.replace('step-', ''));
      const stepColumn = stepColumns.find(col => col.step === newStep);
      
      if (stepColumn) {
        onMoveSubtask(activeId, newStep, stepColumn.step_name);
      }
    }
  };

  const existingSteps = availableSteps.map(step => step.step);
  const canDeleteStep = stepColumns.length > 1;

  return (
    <div className="flex flex-col h-full">
      {/* Step Management Header - Right aligned with Add Subtask button */}
      <div className="flex items-center justify-between mb-4 px-2 flex-shrink-0">
        <div className="text-white text-sm">
          Steps Workflow ({stepColumns.length} step{stepColumns.length !== 1 ? 's' : ''})
        </div>
        <StepManager 
          onAddStep={onAddStep} 
          existingSteps={existingSteps} 
          isRestrictedUser={isRestrictedUser}
        />
      </div>

      {/* Steps Container with Horizontal Scroll - Full height */}
      <div className="flex-1 overflow-hidden min-h-0">
        <div 
          className="h-full overflow-x-auto overflow-y-hidden pb-4"
          style={{ 
            scrollbarWidth: 'thin',
            scrollbarColor: 'primary #000000'
          }}
        >
          <DndContext
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <div className="flex h-full gap-4 pb-2" style={{ minWidth: `${stepColumns.length * 320}px` }}>
              {stepColumns.map((stepColumn) => (
              <StepColumn
                key={stepColumn.step}
                stepColumn={stepColumn}
                onEdit={onEdit}
                onDelete={onDelete}
                onStatusChange={onStatusChange}
                onDeleteStep={onDeleteStep}
                onEditStep={onEditStep}
                canDeleteStep={canDeleteStep}
                isRestrictedUser={isRestrictedUser}
              />
              ))}
            </div>
            
            {/* Enhanced Drag Overlay */}
            {createPortal(
              <DragOverlay>
                {activeSubtask && (
                  <div className="opacity-95 rotate-2 scale-105 transform shadow-2xl z-50">
                    <DraggableSubtask
                      subtask={activeSubtask}
                      onEdit={() => {}}
                      onDelete={() => {}}
                      onStatusChange={() => {}}
                    />
                  </div>
                )}
              </DragOverlay>,
              document.body
            )}
          </DndContext>
        </div>
      </div>

      {subtasks.length === 0 && (
        <div className="text-center text-gray-300 text-sm py-8">
          No subtasks found for this task.
        </div>
      )}

      <style>
        {`
          .steps-container::-webkit-scrollbar {
            height: 8px;
          }
          .steps-container::-webkit-scrollbar-track {
            background: #374151;
            border-radius: 4px;
          }
          .steps-container::-webkit-scrollbar-thumb {
            background: #9CA3AF;
            border-radius: 4px;
          }
          .steps-container::-webkit-scrollbar-thumb:hover {
            background: #D1D5DB;
          }
        `}
      </style>
    </div>
  );
};

export default StepsContainer;
